import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/layout/AppHeader';
import UploadTab from '../components/upload/UploadTab';
import ConnectionsTab from '../components/connections/ConnectionsTab';
import HistoryTab from '../components/history/HistoryTab';
import ProfileTab from '../components/profile/ProfileTab';
import PlatformConsentModal from '../components/connections/PlatformConsentModal';
import { Loader, CheckCircle, AlertCircle } from 'lucide-react';

import { formatFileSize, formatDate } from '../utils/formatters';
import facebookAuth from '../services/facebookAuth';
import youtubeAuth from '../services/youtubeAuth';
import youtubeAPI from '../services/youtubeAPI';
import tiktokAuth from '../services/tiktokAuth';
import tiktokAPI from '../services/tiktokAPI';
import { supabase } from '../services/supabaseClient';
import facebookGraphAPI from '../services/facebookGraphAPI';
import facebookPageSettingsService from '../services/facebookPageSettingsService';
import uploadHistoryService from '../services/uploadHistoryService';
import tiktokSettingsService from '../services/tiktokSettingsService';
function MainApp() {
  const { t } = useTranslation();
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [platforms, setPlatforms] = useState({
    youtube: false,
    facebook: false,
    instagram: false,
    tiktok: false
  });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingPlatform, setPendingPlatform] = useState(null);
  const [connectedPlatforms, setConnectedPlatforms] = useState({
    youtube: false,
    facebook: false,
    instagram: false,
    tiktok: false
  });
  const [uploadStatus, setUploadStatus] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('upload');
  const [facebookPages, setFacebookPages] = useState([]);
  const [selectedFacebookPage, setSelectedFacebookPage] = useState(null);
  const [facebookUserName, setFacebookUserName] = useState(null);
  const [youtubeChannel, setYoutubeChannel] = useState(null);
  
  // TikTok-specific state
  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState(null);
  const [tiktokSettings, setTiktokSettings] = useState({
    privacyLevel: '', // No default - user must select
    disableComment: true, // Default OFF
    disableDuet: true, // Default OFF
    disableStitch: true, // Default OFF
    brandContentToggle: false,
    yourBrand: false,
    brandedContent: false
  });

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
      if (file.size > MAX_SIZE) {
        alert(t('app.main.errors.fileTooLarge', { fileSize: formatFileSize(file.size) }));
        // don't accept the file
        return;
      }

      setVideo(file);
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
    } else {
      alert(t('app.main.errors.invalidVideoFile'));
    }
  };

  const handleClearVideo = () => {
    setVideo(null);
    setVideoPreview(null);
  };

  const handleSelectFacebookPage = async (page) => {
    setSelectedFacebookPage(page);
    // Save to Supabase
    await facebookPageSettingsService.saveSelectedPage(page);
    // Update Instagram connection based on whether the page has Instagram linked
    const hasInstagram = !!page?.instagram_business_account;
    setConnectedPlatforms(prev => ({
      ...prev,
      instagram: hasInstagram
    }));
    // If Instagram was selected but new page doesn't have it, deselect Instagram
    if (!hasInstagram) {
      setPlatforms(prev => ({
        ...prev,
        instagram: false
      }));
    }
  };

  // Helper function to load Facebook pages and selected page
  const loadFacebookPagesAndSelection = async () => {
    try {
      // Check if token is expired first
      if (facebookAuth.isTokenExpired()) {
        console.warn('Facebook token is expired, skipping page load');
        return false;
      }

      // Fetch personal profile name as fallback display name
      const userInfo = await facebookGraphAPI.getMe();
      if (userInfo && userInfo.name) {
        setFacebookUserName(userInfo.name);
      }

      const pages = await facebookGraphAPI.getPages();
      if (pages && pages.length > 0) {
        setFacebookPages(pages);
        
        // Try to load selected page from Supabase
        const savedPage = await facebookPageSettingsService.loadSelectedPage();
        let selectedPage = null;
        
        if (savedPage && pages.some(p => p.id === savedPage.id)) {
          // Refresh the saved page data with current data from API
          selectedPage = pages.find(p => p.id === savedPage.id);
          console.info('[FBPageSettings] Reloaded selected page from Supabase:', selectedPage);
        } else {
          // Default to first page
          selectedPage = pages[0];
          console.info('[FBPageSettings] No saved page found or not in list, defaulting to first:', selectedPage);
          // Save the default selection
          await facebookPageSettingsService.saveSelectedPage(selectedPage);
        }
        
        setSelectedFacebookPage(selectedPage);
        
        // Check if selected page has Instagram linked
        const hasInstagram = !!selectedPage.instagram_business_account;
        setConnectedPlatforms(prev => ({
          ...prev,
          instagram: hasInstagram
        }));
        setPlatforms(prev => ({
          ...prev,
          instagram: hasInstagram
        }));
        
        return true;
      }
      return false;
    } catch (e) {
      console.error('Facebook oldal lekérése sikertelen:', e);
      
      // Check if this is a token invalidation error (Error 190, subcode 460)
      // or any authentication-related error
      const errorMessage = e.message || '';
      const isAuthError = errorMessage.includes('validating access token') ||
                         errorMessage.includes('expired') ||
                         errorMessage.includes('invalidated') ||
                         errorMessage.includes('OAuthException');
      
      if (isAuthError) {
        console.warn('Facebook token is invalid - disconnecting');
        // Clear the invalid token
        await facebookAuth.signOut();
        
        // Mark Facebook as not connected
        setConnectedPlatforms(prev => ({
          ...prev,
          facebook: false,
          instagram: false
        }));
        setPlatforms(prev => ({
          ...prev,
          facebook: false,
          instagram: false
        }));
        setFacebookPages([]);
        setSelectedFacebookPage(null);
      }
      return false;
    }
  };

  const togglePlatform = (platform) => {
    if (!connectedPlatforms[platform]) {
      alert(t('app.main.errors.connectPlatformFirst', { platform }));
      return;
    }
    setPlatforms(prev => ({
      ...prev,
      [platform]: !prev[platform]
    }));
  };

  const connectPlatform = (platform) => {
    // Show consent modal first
    setPendingPlatform(platform);
    setShowConsentModal(true);
  };

  const handleConsentConfirm = () => {
    setShowConsentModal(false);
    const platform = pendingPlatform;
    setPendingPlatform(null);

    if (platform === 'facebook' || platform === 'instagram') {
      // Both Facebook and Instagram use the same OAuth flow
      facebookAuth.signInWithFacebook();
      return;
    }

    if (platform === 'youtube') {
      // YouTube uses Google OAuth
      youtubeAuth.signInWithGoogle();
      return;
    }

    if (platform === 'tiktok') {
      // TikTok OAuth
      tiktokAuth.signInWithTikTok();
      return;
    }

    setTimeout(() => {
      setConnectedPlatforms(prev => ({
        ...prev,
        [platform]: true
      }));
      alert(t('app.main.success.platformConnected', { platform }));
    }, 1000);
  };

  const handleConsentCancel = () => {
    setShowConsentModal(false);
    setPendingPlatform(null);
  };

  const disconnectPlatform = async (platform) => {
    if (platform === 'facebook' || platform === 'instagram') {
      // Disconnect Facebook also disconnects Instagram and vice versa
      setConnectedPlatforms(prev => ({
        ...prev,
        facebook: false,
        instagram: false
      }));
      setPlatforms(prev => ({
        ...prev,
        facebook: false,
        instagram: false
      }));
      setFacebookPages([]);
      setSelectedFacebookPage(null);
      setFacebookUserName(null);
      await facebookAuth.signOut();
      console.log('Facebook/Instagram disconnected successfully');
      return;
    }

    if (platform === 'youtube') {
      setConnectedPlatforms(prev => ({
        ...prev,
        youtube: false
      }));
      setPlatforms(prev => ({
        ...prev,
        youtube: false
      }));
      setYoutubeChannel(null);
      await youtubeAuth.signOut();
      console.log('YouTube disconnected successfully');
      return;
    }

    if (platform === 'tiktok') {
      setConnectedPlatforms(prev => ({
        ...prev,
        tiktok: false
      }));
      setPlatforms(prev => ({
        ...prev,
        tiktok: false
      }));
      await tiktokAuth.signOut();
      console.log('TikTok disconnected successfully');
      return;
    }
    
    setConnectedPlatforms(prev => ({
      ...prev,
      [platform]: false
    }));
    setPlatforms(prev => ({
      ...prev,
      [platform]: false
    }));
  };

  // Load upload history from Supabase on mount
  useEffect(() => {
    const loadHistory = async () => {
      const history = await uploadHistoryService.loadHistory();
      setUploadHistory(history);
    };
    loadHistory();
  }, [t]);

  // Handle Google OAuth callback (YouTube)
  useEffect(() => {
    const handleGoogleCallback = async () => {
      // Check if this is a Google OAuth callback (has code in URL)
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      
      if (code && state && state.startsWith('google_oauth_')) {
        const result = await youtubeAuth.handleGoogleCallback();
        if (result && !result.error) {
          setConnectedPlatforms(prev => ({
            ...prev,
            youtube: true
          }));
          setPlatforms(prev => ({
            ...prev,
            youtube: true
          }));
          if (result.channel_info) {
            setYoutubeChannel(result.channel_info);
          }
          alert(t('app.main.success.youtubeConnected'));
        } else if (result && result.error) {
          alert(t('app.main.errors.youtubeConnectFailed', { error: result.error }));
        }
      }
    };
    handleGoogleCallback();
  }, [t]);

  // Handle TikTok OAuth callback
  useEffect(() => {
    const handleTikTokCallback = async () => {
      const result = await tiktokAuth.handleTikTokCallback();
      
      // Only process if this was a TikTok callback (result is not null/undefined)
      if (result) {
        if (result.success) {
          setConnectedPlatforms(prev => ({
            ...prev,
            tiktok: true
          }));
          setPlatforms(prev => ({
            ...prev,
            tiktok: true
          }));
          alert(t('app.main.success.tiktokConnected'));
        } else if (result.error) {
          alert(t('app.main.errors.tiktokConnectFailed', { error: result.error }));
        }
      }
    };
    handleTikTokCallback();
  }, [t]);

  // Handle Facebook OAuth callback
  useEffect(() => {
    const handleFacebookCallback = async () => {
      const result = await facebookAuth.handleFacebookCallback();
      
      // Only process if this was a Facebook callback (result is not null)
      if (result) {
        if (result.error) {
          alert(t('app.main.errors.facebookConnectFailed', { error: result.error }));
          return;
        }
        
        if (result.access_token) {
          // Facebook was just connected, load pages
          setConnectedPlatforms(prev => ({
            ...prev,
            facebook: true
          }));
          setPlatforms(prev => ({
            ...prev,
            facebook: true
          }));
          
          // Load Facebook pages immediately after connection
          await loadFacebookPagesAndSelection();
          
          alert(t('app.main.success.facebookConnected'));
        }
      }
    };
    handleFacebookCallback();
  }, [t]);

  // Detect stored Facebook token and persisted platform tokens on mount
  useEffect(() => {
    const initializePlatformConnections = async () => {
      // First check localStorage for Facebook token
      const fb = facebookAuth.getToken();
      let facebookToken = fb && fb.access_token ? fb : null;
      if (!facebookToken) {
        await facebookAuth.loadTokenFromSupabase();
        facebookToken = facebookAuth.getToken();
      }
      
      // Initialize Facebook if token exists
      if (facebookToken && facebookToken.access_token) {
        setConnectedPlatforms(prev => ({
          ...prev,
          facebook: true
        }));
        setPlatforms(prev => ({
          ...prev,
          facebook: true
        }));
        
        // Restore cached user name immediately (no API call needed)
        if (facebookToken.user_name) {
          setFacebookUserName(facebookToken.user_name);
        }
        
        // Load Facebook pages and selection
        await loadFacebookPagesAndSelection();
      }

      // Check localStorage for YouTube token
      const yt = youtubeAuth.getToken();
      if (yt && yt.access_token) {
        // Set channel info for display even if token is expired
        if (yt.channel_info) {
          setYoutubeChannel(yt.channel_info);
        }
        
        // Only set as connected if token is not expired
        if (!yt.expired) {
          setConnectedPlatforms(prev => ({
            ...prev,
            youtube: true
          }));
          setPlatforms(prev => ({
            ...prev,
            youtube: true
          }));
        }
      }

      // Check localStorage for TikTok token
      const tt = tiktokAuth.getToken();
      if (tt && tt.access_token) {
        setConnectedPlatforms(prev => ({
          ...prev,
          tiktok: true
        }));
        setPlatforms(prev => ({
          ...prev,
          tiktok: true
        }));
      }

      const session = supabase.getSession();
      if (session && session.user && session.user.id) {
        try {
          // Load Facebook token from Supabase if not in localStorage
          if (!fb || !fb.access_token) {
            await facebookAuth.loadTokenFromSupabase();
            const loadedFb = facebookAuth.getToken();
            if (loadedFb && loadedFb.access_token) {
              setConnectedPlatforms(prev => ({
                ...prev,
                facebook: true
              }));
              setPlatforms(prev => ({
                ...prev,
                facebook: true
              }));
              
              // Load Facebook pages and selection
              await loadFacebookPagesAndSelection();
            }
          }

          // Load YouTube token from Supabase if not in localStorage
          if (!yt || !yt.access_token) {
            const loadedYt = await youtubeAuth.loadTokenFromSupabase();
            if (loadedYt && loadedYt.access_token) {
              setConnectedPlatforms(prev => ({
                ...prev,
                youtube: true
              }));
              setPlatforms(prev => ({
                ...prev,
                youtube: true
              }));
              if (loadedYt.channel_info) {
                setYoutubeChannel(loadedYt.channel_info);
              }
            }
          }

          // Load TikTok token from Supabase if not in localStorage
          if (!tt || !tt.access_token) {
            const loadedTt = await tiktokAuth.loadTokenFromSupabase();
            if (loadedTt && loadedTt.access_token) {
              setConnectedPlatforms(prev => ({
                ...prev,
                tiktok: true
              }));
              setPlatforms(prev => ({
                ...prev,
                tiktok: true
              }));
            }
          }

          const tokens = await supabase.fetchPlatformTokens(session.user.id);
          if (tokens && tokens.length) {
            const connected = {};
            tokens.forEach(t => {
              if (t.platform === 'facebook' && t.access_token) {
                connected.facebook = true;
                // Don't automatically set Instagram - will check if page has IG account linked
              }
              if (t.platform === 'instagram' && t.access_token) connected.instagram = true;
              if (t.platform === 'youtube' && t.access_token) connected.youtube = true;
              if (t.platform === 'tiktok' && t.access_token) connected.tiktok = true;
            });
            setConnectedPlatforms(prev => ({ ...prev, ...connected }));
            setPlatforms(prev => ({ ...prev, ...connected }));
            // Removed redundant Facebook page fetch and selection here
          }

          // If there is a pending platform token from an OAuth redirect, persist it now
          const pendingRaw = localStorage.getItem('pending_platform_token');
          if (pendingRaw) {
            try {
              const pending = JSON.parse(pendingRaw);
              if (pending && pending.platform && pending.access_token) {
                await supabase.upsertPlatformToken({ user_id: session.user.id, ...pending });
                localStorage.removeItem('pending_platform_token');
              }
            } catch (e) {
              console.error('Failed to persist pending platform token:', e);
            }
          }
        } catch (e) {
          console.error('Failed to fetch platform tokens:', e);
        }
      }
    };

    initializePlatformConnections();
  }, []);

  // Fetch TikTok creator info when connected (lazy load - only when needed)
  // Don't fetch on mount to avoid token refresh issues
  // The token validation will happen naturally when user tries to upload
  useEffect(() => {
    const fetchTikTokCreatorInfo = async () => {
      if (connectedPlatforms.tiktok) {
        try {
          const creatorInfo = await tiktokAPI.getCreatorInfo();
          setTiktokCreatorInfo(creatorInfo);
          console.log('TikTok creator info:', creatorInfo);
        } catch (error) {
          console.error('Failed to fetch TikTok creator info:', error);
          // Don't auto-disconnect on error - token refresh might resolve it
          // Just clear the creator info, user will get a proper error when uploading if token is truly invalid
          setTiktokCreatorInfo(null);
        }
      } else {
        // Clear creator info when disconnected
        setTiktokCreatorInfo(null);
      }
    };
    
    // Fetch TikTok creator info whenever TikTok is connected
    if (connectedPlatforms.tiktok) {
      fetchTikTokCreatorInfo();
    }
  }, [connectedPlatforms.tiktok]);

  // Load TikTok settings from database when TikTok is connected
  useEffect(() => {
    const loadTikTokSettings = async () => {
      if (connectedPlatforms.tiktok) {
        try {
          const savedSettings = await tiktokSettingsService.loadSettings();
          if (savedSettings) {
            setTiktokSettings(savedSettings);
            console.log('TikTok settings loaded:', savedSettings);
          }
        } catch (error) {
          console.error('Failed to load TikTok settings:', error);
        }
      }
    };
    loadTikTokSettings();
  }, [connectedPlatforms.tiktok]);

  // Save TikTok settings when they change
  const handleTiktokSettingsChange = async (newSettings) => {
    setTiktokSettings(newSettings);
    
    // Save to database
    try {
      await tiktokSettingsService.saveSettings(newSettings);
      console.log('TikTok settings saved to database');
    } catch (error) {
      console.error('Failed to save TikTok settings:', error);
    }
  };

  const simulateUpload = async (platform, delay) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true, platform });
      }, delay);
    });
  };

  const handleUpload = async () => {
    if (!video) {
      alert(t('app.main.errors.selectVideoFirst'));
      return;
    }

    if (!title.trim()) {
      alert(t('app.main.errors.provideTitle'));
      return;
    }

    const selectedPlatforms = Object.keys(platforms).filter(p => platforms[p]);
    if (selectedPlatforms.length === 0) {
      alert(t('app.main.errors.selectAtLeastOnePlatform'));
      return;
    }

    setIsUploading(true);
    setUploadStatus({});

    // Initialize tracking for Supabase
    window.lastUploadedVideoUrl = '';
    window.lastPlatformPostIds = {};
    window.lastPlatformErrors = {};

    const results = {};
    for (const platform of selectedPlatforms) {
      setUploadStatus(prev => ({
        ...prev,
        [platform]: 'uploading'
      }));

      try {
        if (platform === 'facebook') {
          // Real Facebook Page upload
          const result = await uploadToFacebookPage(video, title, description);
          if (result.success) {
            setUploadStatus(prev => ({
              ...prev,
              [platform]: 'success'
            }));
            results[platform] = 'success';
          } else {
            throw new Error(result.error || t('app.main.errors.uploadFailed'));
          }
        } else if (platform === 'instagram') {
          // Real Instagram Reel upload
          const result = await uploadToInstagram(video, title, description);
          if (result.success) {
            setUploadStatus(prev => ({
              ...prev,
              [platform]: 'success'
            }));
            results[platform] = 'success';
          } else {
            throw new Error(result.error || t('app.main.errors.uploadFailed'));
          }
        } else if (platform === 'youtube') {
          // Real YouTube upload
          const result = await uploadToYouTube(video, title, description);
          if (result.success) {
            setUploadStatus(prev => ({
              ...prev,
              [platform]: 'success'
            }));
            results[platform] = 'success';
          } else {
            throw new Error(result.error || t('app.main.errors.uploadFailed'));
          }
        } else if (platform === 'tiktok') {
          // Real TikTok upload - upload to backend first if not already done
          let videoUrl = window.lastUploadedVideoUrl;
          if (!videoUrl) {
            videoUrl = await uploadVideoToBackend(video);
          }
          const result = await uploadToTikTok(videoUrl, title, description);
          if (result.success) {
            setUploadStatus(prev => ({
              ...prev,
              [platform]: 'success'
            }));
            results[platform] = 'success';

            // Start polling for publish status
            if (result.publish_id) {
              pollTikTokStatus(result.publish_id);
            }
          } else {
            throw new Error(result.error || t('app.main.errors.uploadFailed'));
          }
        } else {
          // Simulated upload for other platforms
          await simulateUpload(platform, 2000 + Math.random() * 2000);
          setUploadStatus(prev => ({
            ...prev,
            [platform]: 'success'
          }));
          results[platform] = 'success';
        }
      } catch (error) {
        console.error(`${platform} upload error:`, error);
        setUploadStatus(prev => ({
          ...prev,
          [platform]: 'error'
        }));
        results[platform] = 'error';
        
        // Store error message for Supabase
        window.lastPlatformErrors = window.lastPlatformErrors || {};
        window.lastPlatformErrors[platform] = error.message;
        
        alert(t('app.main.errors.platformUploadFailed', { platform, error: error.message }));
      }
    }

    // Save to Supabase
    const uploadRecord = await uploadHistoryService.saveUpload({
      title,
      description,
      filename: video.name,
      size: video.size,
      videoUrl: window.lastUploadedVideoUrl || '', // Set by uploadVideoToBackend
      platforms: selectedPlatforms,
      results,
      platformPostIds: window.lastPlatformPostIds || {}, // Set during platform uploads
      errors: window.lastPlatformErrors || {} // Set during platform uploads
    });

    if (uploadRecord.success) {
      // Reload history from Supabase
      const history = await uploadHistoryService.loadHistory();
      setUploadHistory(history);
    } else {
      console.error('Failed to save upload to Supabase');
      // Still show in UI even if Supabase save failed
      const newUpload = {
        id: Date.now(),
        title,
        filename: video.name,
        platforms: selectedPlatforms,
        results,
        timestamp: new Date().toISOString(),
        size: video.size
      };
      setUploadHistory(prev => [newUpload, ...prev]);
    }

    // Clean up temp data
    delete window.lastUploadedVideoUrl;
    delete window.lastPlatformPostIds;
    delete window.lastPlatformErrors;

    setIsUploading(false);
    
    setTimeout(() => {
      setVideo(null);
      setVideoPreview(null);
      setTitle('');
      setDescription('');
      setUploadStatus({});
      setActiveTab('history');
    }, 2000);
  };

  const uploadToFacebookPage = async (videoFile, title, description) => {
    try {
      // Use selected page or fetch if not available
      let page = selectedFacebookPage;
      if (!page) {
        const pages = await facebookGraphAPI.getPages();
        if (!pages || pages.length === 0) {
          return {
            success: false,
            error: t('app.main.errors.facebookPageMissingForUpload')
          };
        }
        page = pages[0];
      }

      const pageAccessToken = page.access_token;
      const pageId = page.id;

      // Upload video to backend first to get public URL
      const publicUrl = await uploadVideoToBackend(videoFile);
      
      // Upload to Facebook Page as Reel
      const result = await facebookGraphAPI.uploadFacebookReel(pageId, pageAccessToken, publicUrl, title, description);
      
      // Store platform post ID if successful
      if (result.success && result.id) {
        window.lastPlatformPostIds = window.lastPlatformPostIds || {};
        window.lastPlatformPostIds.facebook = result.id;
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  };

  const uploadToInstagram = async (videoFile, title, description) => {
    try {
      // Use selected page or fetch if not available
      let page = selectedFacebookPage;
      if (!page) {
        const pages = await facebookGraphAPI.getPages();
        if (!pages || pages.length === 0) {
          return {
            success: false,
            error: t('app.main.errors.facebookPageMissingConnectFirst')
          };
        }
        page = pages[0];
      }

      const pageAccessToken = page.access_token;
      const pageId = page.id;

      // Step 2: Check if page has Instagram account
      const igAccount = await facebookGraphAPI.getInstagramAccount(pageId, pageAccessToken);
      
      if (!igAccount || !igAccount.id) {
        return {
          success: false,
          error: t('app.main.errors.instagramBusinessMissing')
        };
      }

      // Upload video to backend first to get public URL
      const publicUrl = await uploadVideoToBackend(videoFile);
      
      // Upload to Instagram Reel
      const result = await facebookGraphAPI.uploadInstagramReel(igAccount.id, pageAccessToken, publicUrl, description);
      
      // Store platform post ID if successful
      if (result.success && result.id) {
        window.lastPlatformPostIds = window.lastPlatformPostIds || {};
        window.lastPlatformPostIds.instagram = result.id;
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  };

  const uploadToYouTube = async (videoFile, title, description) => {
    try {
      // Upload directly to YouTube using the API
      const result = await youtubeAPI.uploadVideo(videoFile, title, description, {
        privacyStatus: 'public'
      });
      
      // Store platform post ID if successful
      if (result.success && result.videoId) {
        window.lastPlatformPostIds = window.lastPlatformPostIds || {};
        window.lastPlatformPostIds.youtube = result.videoId;
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  };

  const uploadToTikTok = async (videoUrl, title, description) => {
    try {
      // Validate TikTok settings
      if (!tiktokSettings.privacyLevel) {
        throw new Error(t('app.main.errors.selectTiktokVisibility'));
      }

      if (tiktokSettings.brandContentToggle && !tiktokSettings.yourBrand && !tiktokSettings.brandedContent) {
        throw new Error(t('app.main.errors.selectContentType'));
      }

      const options = {
        privacy_level: tiktokSettings.privacyLevel,
        disable_comment: tiktokSettings.disableComment,
        disable_duet: tiktokSettings.disableDuet,
        disable_stitch: tiktokSettings.disableStitch,
        brand_content_toggle: tiktokSettings.brandedContent,
        brand_organic_toggle: tiktokSettings.yourBrand
      };

      const result = await tiktokAPI.uploadVideo(videoUrl, title, description, options);
      
      // Store platform post ID if successful
      if (result.success && result.publish_id) {
        window.lastPlatformPostIds = window.lastPlatformPostIds || {};
        window.lastPlatformPostIds.tiktok = result.publish_id;
      }
      
      return result;
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  };

  // Poll TikTok publish status
  const pollTikTokStatus = async (publishId, attempts = 0) => {
    const maxAttempts = 20; // Poll for up to 20 attempts (about 2 minutes)
    const pollInterval = 6000; // 6 seconds

    if (attempts >= maxAttempts) {
      console.log('TikTok status polling timeout');
      alert(t('app.main.tiktok.processingTimeout'));
      return;
    }

    try {
      const status = await tiktokAPI.checkPublishStatus(publishId);
      console.log('TikTok status:', status);

      if (status.status === 'PUBLISH_COMPLETE') {
        alert(t('app.main.tiktok.publishComplete'));
      } else if (status.status === 'FAILED') {
        alert(t('app.main.tiktok.publishFailed', { reason: status.fail_reason || t('app.main.errors.unknownError') }));
      } else if (status.status === 'PROCESSING_UPLOAD' || status.status === 'PROCESSING_DOWNLOAD' || status.status === 'SEND_TO_USER_INBOX') {
        // Still processing, poll again
        setTimeout(() => pollTikTokStatus(publishId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Failed to check TikTok status:', error);
      // Don't show error to user, just stop polling
    }
  };

  const uploadVideoToBackend = async (videoFile) => {
    // Upload video to backend server via nginx proxy
    const formData = new FormData();
    formData.append('video', videoFile);

    const response = await fetch('https://toldfel.hu/api/upload-video', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(t('app.main.errors.backendUploadFailed'));
    }

    const data = await response.json();
    
    // Store for Supabase
    window.lastUploadedVideoUrl = data.url;
    
    return data.url; // Public URL of the uploaded video
  };

  const getStatusIcon = (status) => {
    if (status === 'uploading') return <Loader className="animate-spin text-blue-500" size={16} />;
    if (status === 'success') return <CheckCircle className="text-green-500" size={16} />;
    if (status === 'error') return <AlertCircle className="text-red-500" size={16} />;
    return null;
  };
  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex space-x-1 sm:space-x-4 mb-6 border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('upload')}
            className={`pb-3 px-2 sm:px-3 font-medium transition-colors text-sm sm:text-base ${
              activeTab === 'upload'
                ? 'text-sky-600 border-b-2 border-sky-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('app.tabs.upload')}
          </button>
          <button
            onClick={() => setActiveTab('connections')}
            className={`pb-3 px-2 sm:px-3 font-medium transition-colors text-sm sm:text-base ${
              activeTab === 'connections'
                ? 'text-sky-600 border-b-2 border-sky-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('app.tabs.connections')}

          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-3 px-2 sm:px-3 font-medium transition-colors text-sm sm:text-base ${
              activeTab === 'history'
                ? 'text-sky-600 border-b-2 border-sky-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('app.tabs.history')}
          </button>
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-3 px-2 sm:px-3 font-medium transition-colors text-sm sm:text-base ${
              activeTab === 'profile'
                ? 'text-sky-600 border-b-2 border-sky-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('app.tabs.profile')}
          </button>
        </div>

        {activeTab === 'upload' && (
          <UploadTab
            video={video}
            videoPreview={videoPreview}
            onClearVideo={handleClearVideo}
            title={title}
            description={description}
            platforms={platforms}
            connectedPlatforms={connectedPlatforms}
            uploadStatus={uploadStatus}
            isUploading={isUploading}
            onVideoChange={handleVideoChange}
            onTitleChange={(e) => setTitle(e.target.value)}
            onDescriptionChange={(e) => setDescription(e.target.value)}
            onTogglePlatform={togglePlatform}
            onUpload={handleUpload}
            formatFileSize={formatFileSize}
            getStatusIcon={getStatusIcon}
            facebookPages={facebookPages}
            selectedFacebookPage={selectedFacebookPage}
            onSelectFacebookPage={handleSelectFacebookPage}
            facebookUserName={facebookUserName}
            youtubeChannel={youtubeChannel}
            tiktokCreatorInfo={tiktokCreatorInfo}
            tiktokSettings={tiktokSettings}
            onTiktokSettingsChange={handleTiktokSettingsChange}
          />

        )}

        {activeTab === 'connections' && (
          <ConnectionsTab
            connectedPlatforms={connectedPlatforms}
            onConnect={connectPlatform}
            onDisconnect={disconnectPlatform}
            tiktokCreatorInfo={tiktokCreatorInfo}
            tiktokSettings={tiktokSettings}
            onTiktokSettingsChange={handleTiktokSettingsChange}
            youtubeChannel={youtubeChannel}
            facebookPages={facebookPages}
            selectedFacebookPage={selectedFacebookPage}
            onSelectFacebookPage={handleSelectFacebookPage}
          />
        )}

        {activeTab === 'history' && (
          <HistoryTab
            uploadHistory={uploadHistory}
            formatDate={formatDate}
            formatFileSize={formatFileSize}
          />
        )}

        {activeTab === 'profile' && (
          <ProfileTab />
        )}
      </div>

      {/* Platform Consent Modal */}
      {showConsentModal && pendingPlatform && (
        <PlatformConsentModal
          platformName={
            pendingPlatform === 'youtube' ? 'YouTube' :
            pendingPlatform === 'facebook' ? 'Facebook' :
            pendingPlatform === 'instagram' ? 'Instagram' :
            pendingPlatform === 'tiktok' ? 'TikTok' :
            pendingPlatform
          }
          onConfirm={handleConsentConfirm}
          onCancel={handleConsentCancel}
        />
      )}
    </div>
  );
}

export default MainApp;